import { parsePositiveSetting } from './bdLib';

export type { BdAdministration } from '../Types/Common/src/bronchodilator';

export const DEFAULT_BD_WAIT_MINUTES = 15;
export const DEFAULT_BD_MAX_MINUTES = 30;

export const BD_TIME_TOLERANCE_MS = 60_000;

const ONE_MINUTE_MS = 60_000;

const HALF_DAY_MS = 12 * 60 * ONE_MINUTE_MS;

// Default pre/post-BD label tokens (programs override via config_Pre/PostBdLabels).
// Matched lowercased + whole-string.
export const DEFAULT_PRE_BD_LABELS = ['prebd'];
export const DEFAULT_POST_BD_LABELS = ['postbd'];

/** Trim + lowercase a configured token list, dropping non-strings and blanks. */
const normalizeBdLabelTokens = (tokens?: readonly string[] | null): string[] =>
  (tokens ?? [])
    .filter((t): t is string => typeof t === 'string')
    .map(t => t.trim().toLowerCase())
    .filter(Boolean);

/**
 * Configured post-BD tokens. A configured array is authoritative — an explicitly
 * emptied (or all-blank) list means no flow
 */
export const resolvePostBdLabelTokens = (configured?: readonly string[] | null): string[] =>
  configured == null ? [...DEFAULT_POST_BD_LABELS] : normalizeBdLabelTokens(configured);

export const isPostBdSelection = (
  labels?: readonly string[] | null,
  postBdLabels?: readonly string[] | null,
): boolean => {
  const tokens = resolvePostBdLabelTokens(postBdLabels);
  return !!labels?.some(label => typeof label === 'string' && tokens.includes(label.trim().toLowerCase()));
};

/**
 * Whether a session launch must pass through the BD-administration gate: labels
 * mark it post-BD and the maneuver is not SVC. `postBdLabels` is the program's
 * configured vocabulary (`config_PostBdLabels`); omit for defaults.
 */
export const shouldGateBdAdministration = (
  labels?: readonly string[] | null,
  pftType?: string,
  postBdLabels?: readonly string[] | null,
): boolean => isPostBdSelection(labels, postBdLabels) && pftType !== 'svc';

export interface BdWaitFeatureFields {
  config_BdWaitMinutes?: number | string | null;
  config_BdMaxMinutes?: number | string | null;
}

export interface BdWaitConfig {
  waitMs: number;
  maxMs: number;
}

export const resolveBdWaitConfig = (featureSet?: unknown): BdWaitConfig => {
  const fields = featureSet as BdWaitFeatureFields | null | undefined;
  const waitMinutes = parsePositiveSetting(fields?.config_BdWaitMinutes) ?? DEFAULT_BD_WAIT_MINUTES;
  const maxMinutes = parsePositiveSetting(fields?.config_BdMaxMinutes) ?? DEFAULT_BD_MAX_MINUTES;
  const waitMs = waitMinutes * ONE_MINUTE_MS;
  return { waitMs, maxMs: Math.max(maxMinutes * ONE_MINUTE_MS, waitMs) };
};

export type BdAdministrationState =
  | { kind: 'future'; startsInMs: number }
  | { kind: 'start-timer'; remainingMs: number; elapsedMs: number }
  | { kind: 'timer-in-progress'; remainingMs: number; elapsedMs: number }
  | { kind: 'ready'; elapsedMs: number }
  | { kind: 'too-long'; elapsedMs: number };

export interface EvaluateBdAdministrationArgs {
  administeredAtMs: number;
  nowMs: number;
  waitMs?: number;
  maxMs?: number;
}

export const evaluateBdAdministration = ({
  administeredAtMs,
  nowMs,
  waitMs = DEFAULT_BD_WAIT_MINUTES * ONE_MINUTE_MS,
  maxMs = DEFAULT_BD_MAX_MINUTES * ONE_MINUTE_MS,
}: EvaluateBdAdministrationArgs): BdAdministrationState => {
  const elapsedMs = nowMs - administeredAtMs;
  const effectiveMaxMs = Math.max(maxMs, waitMs);

  if (elapsedMs < -BD_TIME_TOLERANCE_MS) return { kind: 'future', startsInMs: -elapsedMs };

  const remainingMs = Math.min(Math.max(waitMs - elapsedMs, 0), waitMs);
  if (Math.abs(elapsedMs) <= BD_TIME_TOLERANCE_MS && remainingMs > 0) {
    // Clamp so every returned state guarantees elapsedMs >= 0, even for a within-tolerance future timestamp.
    return { kind: 'start-timer', remainingMs, elapsedMs: Math.max(0, elapsedMs) };
  }
  if (elapsedMs < waitMs) return { kind: 'timer-in-progress', remainingMs, elapsedMs };
  if (elapsedMs <= effectiveMaxMs) return { kind: 'ready', elapsedMs };
  return { kind: 'too-long', elapsedMs };
};

export const parseBdTimeEntry = (hhmm: string, nowMs: number): number | undefined => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(hhmm?.trim() ?? '');
  if (!match) return undefined;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return undefined;
  const date = new Date(nowMs);
  date.setHours(hours, minutes, 0, 0);
  // Resolve to the nearest occurrence
  if (date.getTime() - nowMs > HALF_DAY_MS) date.setDate(date.getDate() - 1);
  else if (nowMs - date.getTime() > HALF_DAY_MS) date.setDate(date.getDate() + 1);
  return date.getTime();
};

export const formatCountdown = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

export const wholeMinutes = (ms: number): number => Math.max(0, Math.floor(ms / ONE_MINUTE_MS));
